import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Droplets, BookOpen, FlaskConical, GraduationCap, ArrowRight, Loader2,
  Phone, MessageCircle, Lock, X, Award, Video, ClipboardList,
  CheckCircle2, FileSignature,
} from "lucide-react";
import { useClinic } from "../context/ClinicContext";
import { createCheckout } from "../lib/api";
import { Tx } from "../context/AutoTranslateContext";
import MarriageServicesSection from "../components/MarriageServicesSection";
import ShareMenu from "../components/ShareMenu";
import PayPalCheckout from "../components/PayPalCheckout";

const ICONS = { Droplets, BookOpen, FlaskConical, GraduationCap, Video };

const Services = () => {
  const { clinic, pricing } = useClinic();
  const nav = useNavigate();
  // ---- Diploma course modal (step 1) ----
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseFullName, setCourseFullName] = useState("");
  const [courseEmail, setCourseEmail] = useState("");
  const [courseBusy, setCourseBusy] = useState(false);
  // ---- Exam booking modal (step 2) ----
  const [showExamModal, setShowExamModal] = useState(false);
  const [examName, setExamName] = useState("");
  const [examEmail, setExamEmail] = useState("");
  const [examPhone, setExamPhone] = useState("");
  const [examBusy, setExamBusy] = useState(false);
  const [error, setError] = useState("");

  if (!clinic) {
    return <div className="max-w-5xl mx-auto py-20 px-4 text-center text-mute">Loading…</div>;
  }

  const coursePrice = pricing?.diploma_course?.amount ?? 250;
  const examPrice = pricing?.exam_booking?.amount ?? 50;
  const certPrice = pricing?.diploma_certificate?.amount ?? 150;
  const totalPrice = coursePrice + examPrice + certPrice;

  const handleCourseBuy = async () => {
    if (!courseFullName.trim() || !courseEmail.trim() || !courseEmail.includes("@")) {
      setError("Please enter your full name and a valid email.");
      return;
    }
    setError("");
    setCourseBusy(true);
    try {
      const res = await createCheckout({
        product_type: "diploma_course",
        full_name: courseFullName.trim(),
        email: courseEmail.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
      setCourseBusy(false);
    }
  };

  const handleExamBuy = async () => {
    if (!examName.trim() || !examEmail.trim() || !examEmail.includes("@") || !examPhone.trim()) {
      setError("Please enter your name, valid email, and phone (used for the video call).");
      return;
    }
    setError("");
    setExamBusy(true);
    try {
      const res = await createCheckout({
        product_type: "exam_booking",
        full_name: examName.trim(),
        email: examEmail.trim(),
        phone: examPhone.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start exam booking.");
      setExamBusy(false);
    }
  };

  const waUrl = `https://wa.me/${clinic.whatsapp}?text=${encodeURIComponent("As-salamu alaykum, I would like to book an Online Video Ruqyah session.")}`;

  return (
    <div data-testid="services-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      {/* Header */}
      <div className="mb-12 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3"><Tx>What we offer</Tx></p>
        <h1 data-testid="services-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          <Tx>Services rooted in the Sunnah.</Tx>
        </h1>
        <p className="text-mute leading-relaxed text-lg">
          <Tx>In-clinic Hijama, in-clinic or online video Ruqyah, Prophetic herbal consultations, and a structured Hijama Diploma pathway you can study from anywhere.</Tx>
        </p>
      </div>

      {/* Academy hero — sits above the classic clinic-service grid */}
      <div
        data-testid="service-card-academy"
        className="clay-surface rounded-3xl p-8 mb-6 border-2 border-gold/40 bg-gradient-to-br from-cream to-sand relative overflow-hidden"
      >
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <div className="w-16 h-16 rounded-2xl bg-gold text-forest flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-8 h-8" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-gold"><Tx>Online learning</Tx></span>
              <span className="bg-gold text-sand text-[10px] uppercase tracking-widest px-3 py-0.5 rounded-full"><Tx>New</Tx></span>
            </div>
            <p className="arabic text-gold text-xl mb-1">أكاديمية القرآن والعربية</p>
            <h3 className="font-heading text-2xl sm:text-3xl text-forest font-semibold mb-2 leading-tight">
              <Tx>Qur'an & Arabic Academy</Tx>
            </h3>
            <p className="text-sm text-mute leading-relaxed">
              <Tx>Recite the Qur'an with colored tajweed and the Tafsīr Al-Muyassar for every ayah. Study alongside Sheikh Al-Husary, Al-Minshawi, Muhammad Jibril and other master teachers. Learn Arabic — alphabet, grammar and top Qur'an vocabulary. Book live 1-to-1 tutors when you're ready.</Tx>
            </p>
          </div>
          <button
            data-testid="open-academy-btn"
            onClick={() => nav("/academy")}
            className="inline-flex items-center gap-2 bg-forest text-sand rounded-full px-6 py-3 hover:bg-sage transition text-sm font-medium whitespace-nowrap"
          >
            <Tx>Open Academy</Tx>
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        </div>
      </div>

      {/* Main service cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14 stagger">
        {clinic.services.filter((s) => s.id !== "diploma").map((s) => {
          const Icon = ICONS[s.icon] || FlaskConical;
          const isOnlineRuqyah = s.id === "ruqyah_online";
          return (
            <div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className="clay-surface rounded-3xl p-8 lift-on-hover relative overflow-hidden"
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isOnlineRuqyah ? "bg-gold text-forest" : "bg-forest/10 text-forest"}`}>
                  <Icon className="w-7 h-7" />
                </div>
                {isOnlineRuqyah && (
                  <span className="bg-gold text-sand text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">New · Online</span>
                )}
              </div>
              <p className="arabic text-gold text-xl mb-2">{s.arabic}</p>
              <h3 className="font-heading text-2xl text-forest font-semibold mb-3 leading-tight">{s.title}</h3>
              <p className="text-sm text-mute leading-relaxed mb-5">{s.description}</p>
              {s.modes && (
                <ul className="text-xs text-mute mb-5 space-y-1.5">
                  {s.modes.map((m) => (
                    <li key={m} className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-forest" /> {m}
                    </li>
                  ))}
                </ul>
              )}
              <a
                href={isOnlineRuqyah ? waUrl : `https://wa.me/${clinic.whatsapp}?text=${encodeURIComponent("As-salamu alaykum, I would like to book a " + s.title + " session.")}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`book-${s.id}`}
                className={`inline-flex items-center gap-2 font-medium text-sm transition-all ${isOnlineRuqyah ? "bg-forest text-sand rounded-full px-5 py-2.5 hover:bg-sage" : "text-forest hover:gap-3"}`}
              >
                {isOnlineRuqyah ? "Book Online Ruqyah via WhatsApp" : "Book a session"}
                <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </a>
            </div>
          );
        })}
      </div>

      {/* ============== Hijama Diploma — 3 STEP JOURNEY ============== */}
      <section data-testid="diploma-pathway" className="mb-14">
        <div className="rounded-3xl bg-gradient-to-br from-forest via-forest to-sage text-sand p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-islamic-pattern opacity-10 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.25em] mb-4">
              <GraduationCap className="w-3 h-3" /> <Tx>Diploma Pathway</Tx>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
              <Tx>Become a Qualified Hijama Practitioner</Tx>
            </h2>
            <p className="text-sand/80 max-w-2xl leading-relaxed mb-2">
              <Tx>A structured three-step journey: study, practical exam, and signed certificate. Each step is paid separately — total path is £{totalPrice.toFixed(2)} if you complete all three.</Tx>
            </p>
            <p className="text-sand/60 text-xs italic mb-8">
              <Tx>Note: between the course and the exam, students must practice on real patients with a qualified Hijama practitioner of their choice.</Tx>
            </p>

            {/* The 3 steps */}
            <div className="grid md:grid-cols-3 gap-5">
              {/* Step 1 — Course PDF */}
              <div data-testid="diploma-step-1" className="bg-sand/95 text-ink rounded-2xl p-6 flex flex-col relative">
                <div className="absolute top-3 right-3"><ShareMenu title="Hijama Diploma Course PDF" compact /></div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-full bg-forest text-sand text-xs font-bold flex items-center justify-center">1</span>
                  <BookOpen className="w-4 h-4 text-forest" />
                  <span className="text-[10px] uppercase tracking-widest text-gold"><Tx>Study material</Tx></span>
                </div>
                <h3 className="font-heading text-xl text-forest font-semibold mb-2"><Tx>Hijama Diploma Course PDF</Tx></h3>
                <p className="text-xs text-mute leading-relaxed mb-4 flex-1">
                  <Tx>Watermarked, 223-page comprehensive course covering theory, the complete 200-point atlas, Sunnah methodology, sterile practice, clinical protocols, and case studies. Yours forever.</Tx>
                </p>
                <p className="font-heading text-2xl text-forest font-bold mb-3">£{coursePrice.toFixed(2)}</p>
                <button
                  data-testid="buy-diploma-course"
                  onClick={() => setShowCourseModal(true)}
                  className="w-full bg-forest text-sand hover:bg-sage rounded-full py-2.5 text-xs font-medium transition flex items-center justify-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5" /> <Tx>Buy course</Tx>
                </button>
              </div>

              {/* Step 2 — Exam booking */}
              <div data-testid="diploma-step-2" className="bg-sand/95 text-ink rounded-2xl p-6 flex flex-col relative">
                <div className="absolute top-3 right-3"><ShareMenu title="Book Hijama Video Practical Exam" compact /></div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-full bg-forest text-sand text-xs font-bold flex items-center justify-center">2</span>
                  <Video className="w-4 h-4 text-forest" />
                  <span className="text-[10px] uppercase tracking-widest text-gold"><Tx>Practical exam</Tx></span>
                </div>
                <h3 className="font-heading text-xl text-forest font-semibold mb-2"><Tx>Book Video Practical Exam</Tx></h3>
                <p className="text-xs text-mute leading-relaxed mb-4 flex-1">
                  <Tx>A 30-minute video call where you demonstrate your practical knowledge. We confirm the date by WhatsApp after booking.</Tx>
                </p>
                <p className="font-heading text-2xl text-forest font-bold mb-3">£{examPrice.toFixed(2)}</p>
                <button
                  data-testid="book-exam"
                  onClick={() => setShowExamModal(true)}
                  className="w-full bg-forest text-sand hover:bg-sage rounded-full py-2.5 text-xs font-medium transition flex items-center justify-center gap-2"
                >
                  <ClipboardList className="w-3.5 h-3.5" /> <Tx>Book exam</Tx>
                </button>
              </div>

              {/* Step 3 — Certificate */}
              <div data-testid="diploma-step-3" className="bg-sand/95 text-ink rounded-2xl p-6 flex flex-col relative">
                <div className="absolute top-3 right-3"><ShareMenu title="Islamic Cure & Care — Hijama Diploma Certificate" compact /></div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-full bg-gold text-forest text-xs font-bold flex items-center justify-center">3</span>
                  <FileSignature className="w-4 h-4 text-gold" />
                  <span className="text-[10px] uppercase tracking-widest text-gold"><Tx>Certificate</Tx></span>
                </div>
                <h3 className="font-heading text-xl text-forest font-semibold mb-2"><Tx>Signed & Stamped Certificate</Tx></h3>
                <p className="text-xs text-mute leading-relaxed mb-4 flex-1">
                  <Tx>After passing the exam we send you a unique link by email to purchase your hand-signed, stamped certificate — delivered as a high-resolution PDF.</Tx>
                </p>
                <p className="font-heading text-2xl text-forest font-bold mb-3">£{certPrice.toFixed(2)}</p>
                <button
                  data-testid="cert-unlock-info"
                  disabled
                  className="w-full bg-mute/15 text-mute cursor-not-allowed rounded-full py-2.5 text-xs font-medium flex items-center justify-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5" /> <Tx>Unlocks after passing</Tx>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="bg-clay/10 border border-clay/30 rounded-2xl p-4 text-clay text-sm mb-6">{error}</div>
      )}

      {/* ===== Course Modal ===== */}
      {showCourseModal && (
        <Modal onClose={() => !courseBusy && setShowCourseModal(false)} testid="diploma-course-modal">
          <ModalHeader icon={Award} label={`Hijama Diploma Course · £${coursePrice.toFixed(2)}`} title="Buy the Course PDF" />
          <p className="text-sm text-mute leading-relaxed mb-5">
            Enter your details. We use your full name on the watermark; you'll be able to download
            the 223-page course PDF immediately after payment.
          </p>
          <Input testid="course-fullname" label="Full name" value={courseFullName} onChange={setCourseFullName} placeholder="e.g., Ahmad Ibn Yusuf" />
          <Input testid="course-email" type="email" label="Email" value={courseEmail} onChange={setCourseEmail} placeholder="you@example.com" />
          <PrimaryBtn testid="course-confirm" busy={courseBusy} onClick={handleCourseBuy}>
            {courseBusy ? "Redirecting to Stripe…" : `Pay with card · £${coursePrice.toFixed(2)}`}
          </PrimaryBtn>
          <div className="mt-3">
            <PayPalCheckout
              productType="diploma_course"
              payload={{ full_name: courseFullName.trim(), email: courseEmail.trim() }}
              disabled={!courseFullName.trim() || !courseEmail.trim() || !courseEmail.includes("@")}
              onPaid={({ order_id }) => nav(`/payment-success?session_id=${order_id}`)}
            />
          </div>
        </Modal>
      )}

      {/* ===== Exam Modal ===== */}
      {showExamModal && (
        <Modal onClose={() => !examBusy && setShowExamModal(false)} testid="exam-modal">
          <ModalHeader icon={Video} label={`Practical Exam Booking · £${examPrice.toFixed(2)}`} title="Book your 30-min video exam" />
          <p className="text-sm text-mute leading-relaxed mb-5">
            We'll contact you on WhatsApp at the phone number you provide to schedule the call.
            The exam covers practical Hijama demonstration based on the course material.
          </p>
          <Input testid="exam-fullname" label="Full name" value={examName} onChange={setExamName} placeholder="e.g., Ahmad Ibn Yusuf" />
          <Input testid="exam-email" type="email" label="Email" value={examEmail} onChange={setExamEmail} placeholder="you@example.com" />
          <Input testid="exam-phone" type="tel" label="Phone (WhatsApp preferred)" value={examPhone} onChange={setExamPhone} placeholder="+44 7..." />
          <PrimaryBtn testid="exam-confirm" busy={examBusy} onClick={handleExamBuy}>
            {examBusy ? "Redirecting to Stripe…" : `Pay with card · £${examPrice.toFixed(2)}`}
          </PrimaryBtn>
          <div className="mt-3">
            <PayPalCheckout
              productType="exam_booking"
              payload={{ full_name: examName.trim(), email: examEmail.trim(), phone: examPhone.trim() }}
              disabled={!examName.trim() || !examEmail.includes("@") || !examPhone.trim()}
              onPaid={({ order_id }) => nav(`/payment-success?session_id=${order_id}`)}
            />
          </div>
        </Modal>
      )}

      {/* Islamic Marriage services: Counselling + Nikah */}
      <MarriageServicesSection />

      {/* Quick book CTA */}
      <div className="rounded-3xl bg-forest text-sand p-10 sm:p-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="relative grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight mb-3">
              <Tx>Ready to book your session?</Tx>
            </h2>
            <p className="text-sand/80 leading-relaxed">
              <Tx>Speak with our team to schedule Hijama, in-clinic or online Ruqyah, or a herbal consultation. Walk-in welcome during clinic hours — call ahead to confirm availability.</Tx>
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <a href={`tel:${clinic.phone_landline}`} data-testid="cta-call" className="inline-flex items-center justify-center gap-2 bg-sand text-forest hover:bg-cream rounded-full px-6 py-3.5 text-sm font-medium">
              <Phone className="w-4 h-4" /> <bdi dir="ltr">{clinic.phone_landline_display}</bdi>
            </a>
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center justify-center gap-2 bg-gold text-sand hover:bg-gold/90 rounded-full px-6 py-3.5 text-sm font-medium">
              <MessageCircle className="w-4 h-4" /> <Tx>WhatsApp us</Tx>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// === Reusable mini-components ===
const Modal = ({ children, onClose, testid }) => (
  <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest/40 backdrop-blur-sm" onClick={onClose}>
    <div className="clay-surface rounded-3xl p-8 sm:p-10 max-w-lg w-full relative animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-forest" aria-label="Close">
        <X className="w-5 h-5" />
      </button>
      {children}
    </div>
  </div>
);

const ModalHeader = ({ icon: Icon, label, title }) => (
  <div className="flex items-start gap-4 mb-5">
    <div className="w-12 h-12 rounded-2xl bg-forest text-sand flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-widest text-gold mb-1">{label}</p>
      <h3 className="font-heading text-2xl text-forest font-semibold leading-tight">{title}</h3>
    </div>
  </div>
);

const Input = ({ label, value, onChange, placeholder, type = "text", testid }) => (
  <label className="block mb-3">
    <span className="text-xs uppercase tracking-widest text-gold">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-base outline-none focus:border-forest"
    />
  </label>
);

const PrimaryBtn = ({ children, onClick, busy, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={busy}
    className="w-full bg-forest text-sand hover:bg-sage rounded-full py-3.5 text-sm font-medium transition disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
  >
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
    {children}
  </button>
);

export default Services;
